import { useEffect, useState } from 'react';
import { StanDropdown, StanLoader, StanModal, StanProgressBar } from '@/components';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { useBulkCustomerActions } from '../../hooks/useBulkCustomerActions';
import { type EmailFlowOption, useEmailFlowOptions } from '../../hooks/useEmailFlowOptions';
import type { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';

interface AddToEmailFlowModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalCustomers: number;
    filterArray: string | undefined;
}

export function AddToEmailFlowModal({ isOpen, onClose, totalCustomers, filterArray }: AddToEmailFlowModalProps) {
    const [selectedFlow, setSelectedFlow] = useState<EmailFlowOption | null>(null);
    const [error, setError] = useState<string | null>(null);

    const {
        availableFlows,
        dropdownOptions,
        findFlowById,
        isLoading: isLoadingFlows,
    } = useEmailFlowOptions({
        enabled: isOpen,
    });

    const { batchState, isLoading, addToEmailFlow, resetState } = useBulkCustomerActions({
        filterArray,
        totalCustomers,
        onSuccess: () => {
            setSelectedFlow(null);
            setError(null);
            onClose();
        },
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedFlow(null);
            setError(null);
            resetState();
        }
    }, [isOpen, resetState]);

    const handleClose = () => {
        if (!isLoading) {
            setSelectedFlow(null);
            setError(null);
            resetState();
            onClose();
        }
    };

    const handleFlowSelect = (value: DropdownValue) => {
        const flow = findFlowById(value.value);
        if (flow) {
            setSelectedFlow(flow);
            setError(null);
        }
    };

    const handleSubmit = () => {
        if (!selectedFlow) {
            setError('Please select a flow!');
            return;
        }

        setError(null);
        addToEmailFlow(selectedFlow.flow_id);
    };

    const modalTitle = 'Add to Email Flow';
    const customerText = totalCustomers === 1 ? 'customer' : 'customers';
    const modalSubheading = `Add ${totalCustomers} ${customerText} to Email Flow`;

    const currentValue = selectedFlow ? { label: selectedFlow.name, value: String(selectedFlow.flow_id) } : null;

    return (
        <StanModal
            id='add-to-email-flow-modal'
            isOpen={isOpen}
            title={modalTitle}
            onClose={handleClose}
            primaryActionLabel={isLoading ? undefined : 'Add to Email Flow'}
            secondaryActionLabel={isLoading ? undefined : 'Never Mind'}
            primaryActionDisabled={!selectedFlow}
            primaryActionLoading={isLoading}
            onPrimaryAction={handleSubmit}
            onSecondaryAction={handleClose}
            size='md'
        >
            <div className='flex flex-col gap-4'>
                <p className='text-gray text-label-sm-regular text-center'>{modalSubheading}</p>

                {isLoading ? (
                    <div className='flex flex-col items-center gap-6 py-8'>
                        <StanLoader />
                        <StanProgressBar
                            min={0}
                            max={batchState.totalCount}
                            value={batchState.processedCount}
                            className='w-full'
                        />
                        <p className='text-gray text-label-xs-regular text-center'>Processing...</p>
                    </div>
                ) : isLoadingFlows ? (
                    <div className='border-disabled flex h-12 items-center justify-between rounded-md border px-4 opacity-75'>
                        <span className='text-secondary text-body-md'>Loading your flows...</span>
                        <div className='border-brand-main size-4 animate-spin rounded-full border-2 border-t-transparent' />
                    </div>
                ) : (
                    <div className='flex flex-col gap-2'>
                        <DropdownProvider
                            dropdownId='email-flow-select'
                            dropdownName='email-flow-select'
                            initialValue={currentValue}
                        >
                            <StanDropdown
                                id='email-flow-select'
                                name='email-flow-select'
                                options={dropdownOptions}
                                onSelect={handleFlowSelect}
                                placeholder='Select an email flow'
                                currentValue={currentValue}
                            />
                        </DropdownProvider>
                        {error && <p className='text-negative text-label-xs-regular'>{error}</p>}

                        {availableFlows.length === 0 && (
                            <p className='text-gray text-label-xs-regular text-center'>
                                No published email flows available.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </StanModal>
    );
}

AddToEmailFlowModal.displayName = 'AddToEmailFlowModal';
